import { useGrowthBook } from 'fitify-ui-onboarding/src/utils/growthbook/generated'
import { isDefined } from 'fitify-utils/src/properties'
import { useTranslation } from 'next-i18next/pages'
import { useMemo } from 'react'

import type {
  ChoiceCardPageType,
  ChoiceCardVerticalPageProps,
} from 'components/GenderFunnel/ChoiceCardPage/ChoiceCardPage.Types'
import { Funnel, type Question } from 'types/state'
import { useFunnelState } from 'utils/state'

type FunnelType =
  | Funnel.M01
  | Funnel.MILICAF01
  | Funnel.MILICAM01
  | Funnel.F01
  | Funnel.GYMF01
  | Funnel.GYMM01
  | Funnel.HOPIF01
  | Funnel.WALLPIF01
  | 'shared'

const handleChange: ChoiceCardVerticalPageProps<
  Question.AgeBucket,
  FunnelType
>['onChange'] = (array) => {
  return { age_bucket: array[0] }
}

export const usePage = (funnel: FunnelType) => {
  const { t } = useTranslation()
  const { data } = useFunnelState(funnel)
  const growthBook = useGrowthBook()
  const title = useMemo(() => {
    switch (funnel) {
      case Funnel.GYMF01:
      case Funnel.GYMM01:
        return t('welcome_gym_meta_title')
      case Funnel.HOPIF01:
        return t('welcome_meta_title_home_pilates')
      case Funnel.WALLPIF01:
        return t('welcome_meta_title_wallpi')
      case Funnel.MILICAF01:
      case Funnel.MILICAM01:
        return t('welcome_meta_title_milica')
      default:
        return t('welcome_meta_title_calisthenics')
    }
  }, [funnel, t])

  const { heading, titleAlign, titleSize, subtitle } = useMemo<
    Pick<
      ChoiceCardPageType<Question.AgeBucket, FunnelType>,
      'heading' | 'titleAlign' | 'titleSize' | 'subtitle'
    >
  >(() => {
    if (funnel === 'shared') {
      return {
        heading: t('welcome_age_buckets_title_calisthenics'),
        titleAlign: 'center',
        titleSize: 'h1',
        subtitle: t('welcome_age_buckets_subtitle'),
      }
    }

    const heading = (() => {
      switch (
        growthBook.evalFeature('onboarding_welcome_age_buckets_title_variant')
          ?.value
      ) {
        case 'workout_plan':
          switch (funnel) {
            case Funnel.GYMF01:
            case Funnel.GYMM01:
              return t('welcome_age_buckets_gym_title')
            case Funnel.HOPIF01:
              return t('welcome_age_buckets_title_home_pilates')
            case Funnel.WALLPIF01:
              return t('welcome_age_buckets_title_wallpi')
            case Funnel.MILICAF01:
            case Funnel.MILICAM01:
              return t('welcome_age_buckets_title_milica')
            default:
              return t('welcome_age_buckets_title_calisthenics')
          }
        case 'challenge_28_day':
          switch (funnel) {
            case Funnel.GYMF01:
            case Funnel.GYMM01:
              return t('welcome_age_buckets_gym_title_challenge')
            case Funnel.HOPIF01:
              return t('welcome_age_buckets_title_home_pilates_challenge')
            case Funnel.WALLPIF01:
              return t('welcome_age_buckets_title_wallpi_challenge')
            case Funnel.MILICAF01:
            case Funnel.MILICAM01:
              return t('welcome_age_buckets_title_milica_challenge')
            default:
              return t('welcome_age_buckets_title_calisthenics_challenge')
          }
        default:
          return t('welcome_how_old_are_you_title')
      }
    })()

    return {
      heading,
      titleAlign: 'center',
      titleSize: 'h1',
      subtitle: t('welcome_age_buckets_select_age'),
    }
  }, [funnel, growthBook, t])

  return useMemo(() => {
    return {
      onChange: handleChange,
      title,
      heading,
      titleAlign,
      titleSize,
      subtitle,
      value: isDefined(data.age_bucket) ? [data.age_bucket] : [],
    } satisfies Omit<
      ChoiceCardPageType<Question.AgeBucket, FunnelType>,
      'funnel' | 'options'
    >
  }, [data.age_bucket, heading, subtitle, title, titleAlign, titleSize])
}
